import { MedplumClient } from "@medplum/core";

import { PDFDocument } from "@pdfme/pdf-lib";
import * as Sentry from "@sentry/react";

import { IMAGE_FILE_TYPES } from "@/types/uploads";
import { drawImageToPdf, mergePdfPages } from "@/utils/pdf";
import { getBinaryIdFromAttachmentUrl } from "@/utils/uploads";

import { FileAttachment } from "../services/file-attachments";
import { LabReportAttachment } from "../services/lab-report-attachments";

export interface PdfMergeProgressOptions {
  onProgress?: (progress: number) => void;
}

export interface PdfMergeResult {
  success: boolean;
  mergedPdfBytes?: Uint8Array;
}

/**
 * Merges the client export PDF with lab report attachments and file attachments.
 *
 * Takes the base export PDF (from @react-pdf/renderer) and appends, in order:
 * 1) lab report attachments (PDFs and images),
 * 2) selected file attachments (DocumentReference PDFs and images).
 *
 * @param medplum - Medplum client instance for downloading attachments
 * @param exportPdfBytes - The base export PDF as an ArrayBuffer
 * @param labReportAttachments - Array of lab report attachments to merge
 * @param fileAttachments - Optional array of file (DocumentReference) attachments to merge after lab reports
 * @returns PdfMergeResult with the merged PDF bytes
 */
export async function mergeExportWithAttachments(
  medplum: MedplumClient,
  exportPdfBytes: ArrayBuffer,
  labReportAttachments: LabReportAttachment[],
  fileAttachments: FileAttachment[] = [],
): Promise<PdfMergeResult> {
  const hasLabReports = labReportAttachments.length > 0;
  const hasFiles = fileAttachments.length > 0;

  if (!hasLabReports && !hasFiles) {
    return {
      success: true,
      mergedPdfBytes: new Uint8Array(exportPdfBytes),
    };
  }

  try {
    const mergedPdf = await PDFDocument.load(exportPdfBytes);

    const mergeAttachment = async (
      attachment: LabReportAttachment["attachment"],
      contentType: string | undefined,
      extra: Record<string, string>,
    ): Promise<void> => {
      const binaryId = getBinaryIdFromAttachmentUrl(attachment.url);
      if (!binaryId) {
        Sentry.captureException(new Error("Binary ID not found for attachment"), { extra });
        return;
      }
      const blob = await medplum.download(`Binary/${binaryId}`);
      const fileBytes = await blob.arrayBuffer();
      if (contentType === "application/pdf") {
        await mergePdfPages(mergedPdf, fileBytes);
      } else if (
        contentType &&
        IMAGE_FILE_TYPES.includes(contentType as (typeof IMAGE_FILE_TYPES)[number])
      ) {
        await drawImageToPdf(mergedPdf, contentType, fileBytes);
      }
    };

    for (const { attachment, diagnosticReportId, labOrderId } of labReportAttachments) {
      const contentType = attachment.contentType;
      try {
        await mergeAttachment(attachment, contentType, { diagnosticReportId, labOrderId });
      } catch (error) {
        Sentry.captureException(error, {
          extra: { diagnosticReportId, labOrderId, contentType },
        });
      }
    }

    for (const { attachment, documentReferenceId } of fileAttachments) {
      const contentType = attachment.contentType;
      try {
        await mergeAttachment(attachment, contentType, { documentReferenceId });
      } catch (error) {
        Sentry.captureException(error, {
          extra: { documentReferenceId, contentType },
        });
      }
    }

    const mergedPdfBytes = await mergedPdf.save();
    return {
      success: true,
      mergedPdfBytes,
    };
  } catch (error) {
    Sentry.captureException(error);
    return {
      success: false,
    };
  }
}
